/**
 * Post-signup pricing screen — anniversary billing, optional promotion code.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../../api/client'
import { startBillingCheckout } from '../../api/billing'
import '../../components/billing/subscriptionBilling.css'

interface BillingPlan {
  configured: boolean
  amount_cents: number
  currency: string
  interval: string
  billing_model: string
}

function formatPlanAmount(cents: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

export default function SubscriptionPricingScreen() {
  const navigate = useNavigate()
  const [promo, setPromo] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const { data: plan } = useQuery({
    queryKey: ['billing-plan'],
    queryFn: () => apiFetch<{ data: BillingPlan }>('GET', '/api/billing/plan').then((r) => r.data),
    staleTime: 60_000,
  })

  const amountLabel = plan ? `${formatPlanAmount(plan.amount_cents, plan.currency)}/${plan.interval}` : '—'

  async function onContinue() {
    setBusy(true)
    setErr(null)
    try {
      const url = await startBillingCheckout(promo.trim() || undefined)
      window.location.href = url
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Checkout failed')
      setBusy(false)
    }
  }

  return (
    <div className="subscription-pricing-page" data-testid="subscription-pricing-page">
      <div className="subscription-pricing-card">
        <h1>Choose your plan</h1>
        <p className="subscription-pricing-lead">
          SOVIQ AI for contractors — full access to pay apps, Hub, and Soren.
        </p>
        <div className="subscription-pricing-plan" data-testid="subscription-pricing-amount">
          <span className="subscription-pricing-price">{amountLabel}</span>
          <span className="subscription-pricing-note">Charged today, then on this day each month.</span>
        </div>
        <label className="subscription-pricing-promo-label" htmlFor="promo-code">
          Promotion code
        </label>
        <input
          id="promo-code"
          className="subscription-pricing-promo-input"
          data-testid="subscription-promo-input"
          value={promo}
          onChange={(e) => setPromo(e.target.value)}
          placeholder="Have a code? Enter it here"
          autoComplete="off"
        />
        <button
          type="button"
          className="btn primary subscription-pricing-cta"
          data-testid="subscription-pricing-continue"
          disabled={busy}
          onClick={() => void onContinue()}
        >
          {busy ? 'Opening checkout…' : 'Continue to checkout'}
        </button>
        <p className="subscription-pricing-footnote">Card required · cancel anytime in Settings → Billing</p>
        {err && <p className="subscription-pricing-error">{err}</p>}
        <button type="button" className="btn ghost btn-sm" onClick={() => navigate('/settings?panel=billing')}>
          Back to billing settings
        </button>
      </div>
    </div>
  )
}
